import { EyePropertyCode } from 'src/models/Eye.model';

import { Exam, ExamStepEnum, OrderIDB, outboxDb, UploadStateEnum } from './db';

export const RESUMABLE_WINDOW_MS = 72 * 60 * 60 * 1000;

export interface CreateOrderInput {
  OrderId: string;
  OrderStatus: OrderIDB['OrderStatus'];
  ExamId: string;
  SiteId?: string;
}

function buildOrder(input: CreateOrderInput): OrderIDB {
  const now = new Date();
  return {
    OrderId: input.OrderId,
    OrderStatus: input.OrderStatus,
    SiteId: input.SiteId,
    OrderUploadStatus: UploadStateEnum.PENDING,
    LeftImageAttempts: 0,
    RightImageAttempts: 0,
    LeftSubmissions: 0,
    RightSubmissions: 0,
    CreatedAt: now,
    UpdatedAt: now,
    Exams: [
      {
        ExamId: input.ExamId,
        LeftEyeUploadStatus: UploadStateEnum.NOT_STARTED,
        RightEyeUploadStatus: UploadStateEnum.NOT_STARTED,
        ExamUploadStatus: UploadStateEnum.NOT_STARTED,
        ResultPollingStatus: UploadStateEnum.NOT_STARTED,
        UpdatedAt: now,
      },
    ],
  };
}

export async function createOrderIDB(
  input: CreateOrderInput,
): Promise<OrderIDB> {
  const order = buildOrder(input);
  await outboxDb.orders.add(order);
  return order;
}

export function getOrCreateOrderIDB(
  input: CreateOrderInput,
): Promise<OrderIDB> {
  return outboxDb.transaction('rw', outboxDb.orders, async () => {
    const existing = await outboxDb.orders.get(input.OrderId);
    if (existing) {
      return existing;
    }
    const order = buildOrder(input);
    await outboxDb.orders.add(order);
    return order;
  });
}

export async function getOrderIDB(
  orderId: string,
): Promise<OrderIDB | undefined> {
  const order = await outboxDb.orders.get(orderId);
  if (order) {
    // Most-recently-updated exam first. Sorting the freshly-loaded clone does
    // not touch what is persisted.
    order.Exams.sort((a, b) => b.UpdatedAt.getTime() - a.UpdatedAt.getTime());
  }
  return order;
}

export async function getNewestUnsyncedExamIDB(
  orderId: string,
): Promise<Exam | undefined> {
  const order = await outboxDb.orders.get(orderId);
  return order?.Exams.filter(
    (exam) =>
      exam.LeftEyeUploadStatus === UploadStateEnum.PENDING ||
      exam.RightEyeUploadStatus === UploadStateEnum.PENDING ||
      exam.ExamUploadStatus === UploadStateEnum.PENDING,
  ).sort((a, b) => b.UpdatedAt.getTime() - a.UpdatedAt.getTime())[0];
}

export async function getNewestUncompletedExamIDB(
  orderId: string,
): Promise<Exam | undefined> {
  const order = await outboxDb.orders.get(orderId);
  return order?.Exams.filter(
    (exam) => exam.ResultPollingStatus === UploadStateEnum.PENDING,
  ).sort((a, b) => b.UpdatedAt.getTime() - a.UpdatedAt.getTime())[0];
}

export async function getNewestCompletedExamIDB(
  orderId: string,
): Promise<Exam | undefined> {
  const order = await outboxDb.orders.get(orderId);
  return order?.Exams.filter(
    (exam) => exam.ResultPollingStatus === UploadStateEnum.COMPLETED,
  ).sort((a, b) => b.UpdatedAt.getTime() - a.UpdatedAt.getTime())[0];
}

export function deleteOrderIDB(orderId: string): Promise<void> {
  return outboxDb.orders.delete(orderId);
}

export async function pruneExpiredOrdersIDB(): Promise<string[]> {
  const cutoff = new Date(Date.now() - RESUMABLE_WINDOW_MS);
  // Keys only: every row can hold two image blobs, and a range scan over the
  // index never materialises them.
  const expiredOrderIds = await outboxDb.orders
    .where('UpdatedAt')
    .belowOrEqual(cutoff)
    .primaryKeys();

  if (expiredOrderIds.length) {
    await outboxDb.orders.bulkDelete(expiredOrderIds);
  }
  return expiredOrderIds;
}

export function listAllOrdersIDB(): Promise<OrderIDB[]> {
  return outboxDb.orders.orderBy('UpdatedAt').reverse().toArray();
}

export function listPendingOrdersIDB(): Promise<OrderIDB[]> {
  return outboxDb.orders
    .where('OrderUploadStatus')
    .equals(UploadStateEnum.PENDING)
    .toArray();
}

async function mutateOrder(
  orderId: string,
  mutate: (order: OrderIDB) => void,
): Promise<OrderIDB> {
  return outboxDb.transaction('rw', outboxDb.orders, async () => {
    const order = await outboxDb.orders.get(orderId);
    if (!order) {
      throw new Error(`No offline OrderIDB for OrderId=${orderId}`);
    }
    mutate(order);
    order.UpdatedAt = new Date();
    await outboxDb.orders.put(order);
    return order;
  });
}

function findExam(order: OrderIDB, examId: string): Exam {
  const exam = order.Exams.find((e) => e.ExamId === examId);
  if (!exam) {
    throw new Error(`No Exam ${examId} on OrderIDB ${order.OrderId}`);
  }
  return exam;
}

export function setEyeImageIDB(
  orderId: string,
  examId: string,
  side: EyePropertyCode,
  blob: Blob,
  fileName: string,
): Promise<OrderIDB> {
  if (!blob.size) {
    throw new Error('Refusing to store an empty (zero-byte) image blob');
  }
  return mutateOrder(orderId, (order) => {
    const exam = findExam(order, examId);
    if (side === EyePropertyCode.LEFT) {
      exam.LeftEyeImage = blob;
      exam.LeftEyeFileName = fileName;
      exam.LeftEyeUploadStatus = UploadStateEnum.PENDING;
    } else {
      exam.RightEyeImage = blob;
      exam.RightEyeFileName = fileName;
      exam.RightEyeUploadStatus = UploadStateEnum.PENDING;
    }
  });
}

export function markEyeUploadedIDB(
  orderId: string,
  examId: string,
  side: EyePropertyCode,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    const exam = findExam(order, examId);
    if (side === EyePropertyCode.LEFT) {
      exam.LeftEyeUploadStatus = UploadStateEnum.COMPLETED;
    } else {
      exam.RightEyeUploadStatus = UploadStateEnum.COMPLETED;
    }
  });
}

export function updateExamFieldsIDB(
  orderId: string,
  examId: string,
  patch: Partial<Omit<Exam, 'ExamId' | 'UpdatedAt'>>,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    const exam = findExam(order, examId);
    for (const [key, value] of Object.entries(patch)) {
      if (key !== 'ExamId' && value !== undefined) {
        Object.assign(exam, { [key]: value });
      }
    }
    exam.UpdatedAt = new Date();
  });
}

export function setOrderStatusIDB(
  orderId: string,
  status: OrderIDB['OrderStatus'],
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    order.OrderStatus = status;
  });
}

export function setOrderUploadStatusIDB(
  orderId: string,
  status: UploadStateEnum,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    order.OrderUploadStatus = status;
  });
}

export function setOrderLastStepIDB(
  orderId: string,
  step: ExamStepEnum,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    order.LastStep = step;
  });
}

export function markExamCreatedInGalenIDB(
  orderId: string,
  examId: string,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    const exam = findExam(order, examId);
    exam.ExamCreatedInGalen = true;
  });
}

export function setExamUploadStatusIDB(
  orderId: string,
  examId: string,
  status: UploadStateEnum,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    const exam = findExam(order, examId);
    exam.ExamUploadStatus = status;
  });
}

export function setResultPollingStatusIDB(
  orderId: string,
  examId: string,
  status: UploadStateEnum,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    const exam = findExam(order, examId);
    exam.ResultPollingStatus = status;
  });
}

export function incrementLeftImageTakenIDB(orderId: string): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    order.LeftImageAttempts += 1;
  });
}

export function incrementRightImageTakenIDB(
  orderId: string,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    order.RightImageAttempts += 1;
  });
}

export async function getLeftImageTakenIDB(orderId: string): Promise<number> {
  const order = await getOrderIDB(orderId);
  if (!order) {
    throw new Error(`No OrderIDB for OrderId=${orderId}`);
  }
  return order.LeftImageAttempts;
}

export async function getRightImageTakenIDB(orderId: string): Promise<number> {
  const order = await getOrderIDB(orderId);
  if (!order) {
    throw new Error(`No OrderIDB for OrderId=${orderId}`);
  }
  return order.RightImageAttempts;
}

export function incrementLeftImageSubmissionIDB(
  orderId: string,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    order.LeftSubmissions += 1;
  });
}

export function incrementRightImageSubmissionIDB(
  orderId: string,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (order) => {
    order.RightSubmissions += 1;
  });
}

export function addRetakeExamIDB(
  orderId: string,
  sourceExamId: string,
  newExamId: string,
): Promise<OrderIDB> {
  return mutateOrder(orderId, (examOrder) => {
    const sourceExam = examOrder.Exams.find((e) => e.ExamId === sourceExamId);
    examOrder.Exams.push({
      ExamId: newExamId,
      LeftEyeUploadStatus: UploadStateEnum.NOT_STARTED,
      RightEyeUploadStatus: UploadStateEnum.NOT_STARTED,
      ExamUploadStatus: UploadStateEnum.NOT_STARTED,
      ResultPollingStatus: UploadStateEnum.NOT_STARTED,
      UpdatedAt: new Date(),
      TypeOfDiabetes: sourceExam?.TypeOfDiabetes,
      LengthOfDiabetesDiagnosis: sourceExam?.LengthOfDiabetesDiagnosis,
      TimeSinceLastEyeExam: sourceExam?.TimeSinceLastEyeExam,
      LeftEyeMainGain: sourceExam?.LeftEyeMainGain,
      LeftEyeSideGain: sourceExam?.LeftEyeSideGain,
      RightEyeMainGain: sourceExam?.RightEyeMainGain,
      RightEyeSideGain: sourceExam?.RightEyeSideGain,
    });
  });
}

export const outbox = {
  createOrderIDB,
  getOrCreateOrderIDB,
  getOrderIDB,
  getNewestUnsyncedExamIDB,
  getNewestUncompletedExamIDB,
  deleteOrderIDB,
  pruneExpiredOrdersIDB,
  listAllOrdersIDB,
  listPendingOrdersIDB,
  setEyeImageIDB,
  setOrderLastStepIDB,
  markEyeUploadedIDB,
  updateExamFieldsIDB,
  setOrderStatusIDB,
  setOrderUploadStatusIDB,
  markExamCreatedInGalenIDB,
  setExamUploadStatusIDB,
  setResultPollingStatusIDB,
  incrementLeftImageTakenIDB,
  incrementRightImageTakenIDB,
  getLeftImageTakenIDB,
  getRightImageTakenIDB,
  addRetakeExamIDB,
  incrementLeftImageSubmissionIDB,
  incrementRightImageSubmissionIDB,
  getNewestCompletedExamIDB,
};
